"use client";

import { useQuery } from "@tanstack/react-query";
import {
  getUserComedianFollows,
  getUserComedianFollowsById,
  type UserComedianFollow,
} from "@/data/v2/client/user/follows";
import { useUser } from "@punchup/data/legacy/hooks";

interface UseUserComedianFollowsOptions {
  /**
   * Optional user ID to fetch follows for.
   * If not provided, fetches follows for the authenticated user.
   */
  userId?: string;
}

/**
 * Custom hook for fetching user comedian follows with React Query
 *
 * @param options - Configuration options for the hook
 * @param options.userId - Optional user ID to fetch follows for. If not provided, uses authenticated user
 * @param options.enabled - Whether the query should be enabled (default: true)
 *
 * @returns Object containing:
 * - follows: Array of user_comedian_follows rows
 * - followedComedianIds: Array of comedian IDs that the user follows
 * - isFollowing: Function to check if user follows a specific comedian
 * - isLoading: Whether the query is loading
 * - error: Error object if query failed
 * - refetch: Function to refetch the data
 *
 * @example
 * ```tsx
 * // Fetch follows for authenticated user
 * const { follows, isFollowing, isLoading } = useUserComedianFollows();
 *
 * // Check if user follows a specific comedian
 * if (isFollowing('comedian-id-123')) {
 *   // User follows this comedian
 * }
 *
 * // Fetch follows for a specific user
 * const { follows } = useUserComedianFollows({ userId: 'user-id-456' });
 * ```
 */
export function useUserComedianFollows({
  userId,
}: UseUserComedianFollowsOptions = {}) {
  const currentUser = useUser();

  // Determine which user's follows to fetch
  const targetUserId = userId || currentUser?.id;

  const {
    data: follows = [],
    isLoading,
    error,
    refetch,
  } = useQuery<UserComedianFollow[]>({
    queryKey: ["user-comedian-follows", targetUserId],
    queryFn: () =>
      userId ? getUserComedianFollowsById(userId) : getUserComedianFollows(),
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Extract comedian IDs for convenient access
  const followedComedianIds = follows.map((follow) => follow.comedian_id);

  // Helper function to check if user follows a specific comedian
  const isFollowing = (comedianId: string): boolean => {
    return followedComedianIds.includes(comedianId);
  };

  return {
    follows,
    followedComedianIds,
    isFollowing,
    isLoading,
    error,
    refetch,
  };
}
